import type { RightKey } from '@k2/domain/rights'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useOrganizationsResource } from '../resources'

export type Organization = {
  id: string
  name: string
  verified: boolean
  brandCount: number
  invitedAt?: string
}

export type OrganizationDetails = Omit<Organization, 'invitedAt'> & {
  hasBrands: boolean
  timezone: string
  rights: readonly RightKey[]
}

export const useOrganizationStore = defineStore('organization', () => {
  const { getOrganization, getOrganizations, setOrganizationName } = useOrganizationsResource()
  const organizations = ref<Organization[]>([])
  const details = ref<Record<string, OrganizationDetails>>({})
  const loaded = ref(false)

  // No `invitedAt` means the caller created it (`@k2/db` identity.ts).
  const own = computed(() => organizations.value.filter(organization => !organization.invitedAt))
  const invited = computed(() => organizations.value.filter(organization => organization.invitedAt))

  const load = async () => {
    if (loaded.value) return
    const { data } = await getOrganizations()
    organizations.value = data ?? []
    loaded.value = true
  }

  // A 404 is the API's membership answer (`ARCHITECTURE.md` §3.1): keeping the row would let the picker send you straight back.
  const refreshDetails = async (organizationId: string) => {
    const result = await getOrganization(organizationId)
    if (result.data) details.value = { ...details.value, [organizationId]: result.data }
    if (result.status === 404) organizations.value = organizations.value.filter(organization => organization.id !== organizationId)
    return result
  }

  const getOrLoadDetails = async (organizationId: string) => {
    const existing = details.value[organizationId]
    if (existing) return existing

    const result = await refreshDetails(organizationId)
    return result.data ?? undefined
  }

  const rename = async (organizationId: string, name: string) => {
    const result = await setOrganizationName(organizationId, name)
    const renamed = result.data?.name
    if (!renamed) return result
    organizations.value = organizations.value.map(organization =>
      organization.id === organizationId ? { ...organization, name: renamed } : organization
    )
    const existing = details.value[organizationId]
    if (existing) details.value = { ...details.value, [organizationId]: { ...existing, name: renamed } }
    return result
  }

  const hasRight = (organizationId: string, right: RightKey) => details.value[organizationId]?.rights.includes(right) === true

  return { organizations, details, loaded, own, invited, load, refreshDetails, getOrLoadDetails, rename, hasRight }
})
